import { createHouseVisit as apiCreateHouseVisit, ApiError } from './apiService';

/**
 * Cola local + envío para el resultado de una visita
 * (`POST /v1/houses/:houseId/visits`), formulario dinámico
 * `visit-result`. Sigue el mismo patrón offline-first que
 * `houseCaptureService.js`, pero con su propia cola en localStorage ya
 * que cada visita queda asociada a una vivienda (`houseId`).
 */

const QUEUE_KEY = 'pulso17.visits.queue';
export const MAX_QUEUED_VISITS = 10;

/** Error específico para cuando la cola local ya llegó al máximo permitido. */
export class VisitQueueFullError extends Error {
  constructor() {
    super(
      `Ya tienes ${MAX_QUEUED_VISITS} visitas guardadas sin sincronizar. Sincroniza antes de capturar otra.`
    );
    this.name = 'VisitQueueFullError';
  }
}

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Fecha-hora ISO 8601 con offset local, como la espera el backend. */
function formatIsoWithOffset(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`
  );
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/** Visitas guardadas localmente, pendientes de sincronizar. */
export function getQueuedVisits() {
  return readQueue();
}

/** Cantidad de visitas pendientes (para badges/KPIs). */
export function getQueuedVisitsCount() {
  return readQueue().length;
}

/**
 * Arma el objeto de una visita a partir de las respuestas del
 * formulario dinámico `visit-result`. `answersByKey` es un mapa
 * `questionKey -> valor` (ya resuelto por el llamador). `startedAt`
 * debe capturarse cuando se abre el formulario (antes de responder) y
 * se usa junto con el momento del envío para `started_at`/`visited_at`.
 *
 * @param {{
 *   houseId: string|number,
 *   answersByKey: Record<string, any>,
 *   startedAt: Date,
 *   deviceId?: string,
 * }} params
 */
export function buildVisitPayload({ houseId, answersByKey, startedAt, deviceId = 'device-001' }) {
  const visitedAt = formatIsoWithOffset(new Date());

  return {
    _houseId: Number(houseId),
    local_uuid: generateUuid(),
    device_id: deviceId,
    started_at: formatIsoWithOffset(startedAt ?? new Date()),
    visited_at: visitedAt,
    synced_at: null,
    location: {
      latitude: 0,
      longitude: 0,
      accuracy: 0,
      captured_at: visitedAt,
    },
    answers: answersByKey,
  };
}

/**
 * Guarda una visita en la cola local. Lanza `VisitQueueFullError` si
 * ya se alcanzó `MAX_QUEUED_VISITS`.
 *
 * @param {Object} visit ver `buildVisitPayload`.
 */
export function enqueueVisit(visit) {
  const queue = readQueue();
  if (queue.length >= MAX_QUEUED_VISITS) {
    throw new VisitQueueFullError();
  }
  writeQueue([...queue, visit]);
  return visit;
}

/**
 * Sincroniza todas las visitas pendientes, una por una, contra
 * `POST /v1/houses/:houseId/visits`. Las que se envían con éxito se
 * quitan de la cola; si alguna falla, se detiene y la cola conserva
 * los pendientes (incluida la que falló) para reintentar después.
 *
 * @returns {Promise<{ synced: number }>}
 */
export async function syncQueuedVisits() {
  const queue = readQueue();
  let synced = 0;
  const remaining = [...queue];

  while (remaining.length > 0) {
    const visit = remaining[0];
    try {
      const { _houseId: houseId, ...payload } = visit;
      await apiCreateHouseVisit(houseId, { ...payload, synced_at: formatIsoWithOffset(new Date()) });
      remaining.shift();
      synced += 1;
      writeQueue(remaining);
    } catch (err) {
      writeQueue(remaining);
      throw err;
    }
  }

  return { synced };
}

/** `true` si el error viene de que el backend no está disponible. */
export function isNetworkError(err) {
  return err instanceof ApiError && err.status === 0;
}
